import { useState, useEffect } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Bot,
  Puzzle,
  Settings,
  LogOut,
  Menu,
  X,
  FolderKanban,
  Plug,
  Store,
  MessageSquare,
  Wand2,
  LayoutTemplate,
  Search,
  Sparkles,
  ArrowRight,
} from 'lucide-react';
import { useAuth } from '@/lib/auth/context';
import { SidebarNavItem, Avatar, Button } from '@/volt/components';
import { isOnboardingComplete, dismissOnboarding } from '@/lib/onboarding/state';
import { CommandPaletteProvider, useCommandPalette } from '@/components/command-palette/CommandPaletteProvider';

/** Sidebar search hint -- uses command palette context, must render inside CommandPaletteProvider. */
function SearchShortcutHint() {
  const { setOpen } = useCommandPalette();
  return (
    <button
      onClick={() => setOpen(true)}
      className="flex items-center gap-2 w-full px-4 py-2 text-xs text-[var(--volt-text-muted)] hover:text-[var(--volt-text-secondary)] hover:bg-[var(--volt-dark-elevated)] transition-colors"
    >
      <Search className="w-3.5 h-3.5" />
      <span>Search</span>
      <kbd className="ml-auto inline-flex items-center gap-0.5 rounded border border-[var(--volt-dark-border)] bg-[var(--volt-dark)] px-1.5 py-0.5 font-mono text-[10px] text-[var(--volt-text-muted)]">
        <span className="text-xs">&#8984;</span>K
      </kbd>
    </button>
  );
}

const navigation = [
  { name: 'Dashboard', to: '/dashboard', icon: LayoutDashboard },
  { name: 'Chat', to: '/dashboard/chat', icon: MessageSquare },
  { name: 'Projects', to: '/dashboard/projects', icon: FolderKanban },
  { name: 'Skills', to: '/dashboard/skills', icon: Puzzle },
  { name: 'Builder', to: '/dashboard/skills/builder', icon: Wand2 },
  { name: 'Agents', to: '/dashboard/agents', icon: Bot },
  { name: 'Connectors', to: '/dashboard/connectors', icon: Plug },
  { name: 'Templates', to: '/dashboard/templates', icon: LayoutTemplate },
  { name: 'Marketplace', to: '/dashboard/marketplace', icon: Store },
  { name: 'Settings', to: '/dashboard/settings', icon: Settings },
];

export function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(() => !isOnboardingComplete());
  const pathname = useLocation().pathname;
  const { user, logout } = useAuth();

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [pathname]);

  // Body scroll lock when mobile sidebar is open
  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [sidebarOpen]);

  return (
    <CommandPaletteProvider>
    <div className="min-h-screen bg-[var(--volt-dark)]">
      {/* Mobile sidebar backdrop */}
      <div
        className={`fixed inset-0 bg-black/50 z-40 lg:hidden transition-opacity duration-300 ${
          sidebarOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setSidebarOpen(false)}
        aria-hidden="true"
      />

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full w-64 bg-[var(--volt-dark-surface)] border-r border-[var(--volt-dark-border)] transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Sidebar header */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-[var(--volt-dark-border)]">
          <Link to="/dashboard" className="flex items-center gap-2">
            <img
              src="/logo-icon.png"
              alt="FoxGrid"
              width={36}
              height={36}
              className="h-9 w-9"
            />
            <span className="text-xl font-bold tracking-tight text-[var(--volt-text-primary)]">
              Fox<span className="text-orange-500">Grid</span>
            </span>
          </Link>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden min-h-[44px] min-w-[44px] flex items-center justify-center text-[var(--volt-text-muted)] hover:text-[var(--volt-text-primary)] rounded-[var(--volt-radius-md)] hover:bg-[var(--volt-dark-elevated)] transition-colors"
            aria-label="Close sidebar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1">
          {navigation.map((item) => (
            <SidebarNavItem
              key={item.name}
              href={item.to}
              icon={item.icon}
              label={item.name}
              isActive={pathname === item.to}
            />
          ))}
        </nav>

        {/* User menu at bottom */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-[var(--volt-dark-border)]">
          {/* Cmd+K shortcut hint */}
          <SearchShortcutHint />

          <div className="flex items-center gap-3 p-4 pt-2">
            <Avatar
              name={user?.name || user?.email || 'User'}
              size="md"
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[var(--volt-text-primary)] truncate">
                {user?.name || 'User'}
              </p>
              <p className="text-xs text-[var(--volt-text-muted)] truncate">{user?.email}</p>
            </div>
            <button
              onClick={logout}
              className="min-h-[44px] min-w-[44px] flex items-center justify-center text-[var(--volt-text-muted)] hover:text-[var(--volt-error)] rounded-[var(--volt-radius-md)] hover:bg-[var(--volt-dark-elevated)] transition-colors"
              title="Sign out"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top header */}
        <header className="sticky top-0 z-20 bg-[var(--volt-dark-surface)] border-b border-[var(--volt-dark-border)]">
          <div className="h-16 flex items-center px-4 lg:px-6">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden min-h-[44px] min-w-[44px] flex items-center justify-center -ml-2 text-[var(--volt-text-muted)] hover:text-[var(--volt-text-primary)] hover:bg-[var(--volt-dark-elevated)] rounded-[var(--volt-radius-md)] transition-colors"
              aria-label="Open sidebar"
            >
              <Menu className="w-6 h-6" />
            </button>

            <div className="flex-1" />

            {/* Workspace name */}
            <span className="text-sm font-medium text-[var(--volt-text-secondary)] truncate max-w-[200px]">
              {user?.tenantName || user?.name || 'Workspace'}
            </span>
          </div>
          {/* Onboarding banner inside sticky header — always visible */}
          {showOnboarding && (
            <div className="flex items-center justify-between gap-3 px-4 lg:px-6 py-2 bg-[var(--volt-blue-muted)] border-t border-[var(--volt-blue)]">
              <div className="flex items-center gap-2 min-w-0">
                <Sparkles className="w-4 h-4 text-[var(--volt-blue)] flex-shrink-0" />
                <span className="text-sm text-[var(--volt-text-primary)] truncate">
                  <strong>Welcome!</strong> <span className="hidden sm:inline text-[var(--volt-text-secondary)]">Set up your first automation.</span>
                </span>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Link to="/dashboard/onboarding">
                  <Button variant="primary" size="sm">Get Started</Button>
                </Link>
                <button
                  onClick={() => { dismissOnboarding(); setShowOnboarding(false); }}
                  className="text-[var(--volt-text-muted)] hover:text-[var(--volt-text-primary)] transition-colors p-1"
                  aria-label="Dismiss"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}
        </header>

        {/* Page content */}
        <main className="p-4 md:p-6 lg:p-8 bg-[var(--volt-dark)] min-h-[calc(100vh-4rem)]">
          <Outlet />
        </main>
      </div>
    </div>
    </CommandPaletteProvider>
  );
}
